/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-16 11:51:42
 *
 *******************************************************/
//@flow
import React, { createRef } from 'react';
import { ViewMajor } from '@shopify/polaris-icons';
import { ArrowDownMinor, ArrowUpMinor } from '@shopify/polaris-icons';
import { Icon, Link, Modal, SkeletonBodyText, SkeletonDisplayText } from '@shopify/polaris';
import ProductPrice from 'src/components/views/common/product-list/product-price';
import { withI18n } from '@shopify/react-i18n';
import ProductDetail from 'src/components/views/common/product-list/product-detail';
import noProductImage from 'src/assets/images/product/no-product-image.png';

interface ProductListProps {
  products: any[];
  onloadMore: () => void;
  loading?: boolean;
  hideLoadMore?: boolean;
  page: number;
  perPage: number;
}

const ProductList = (props: ProductListProps) => {
  const {
    i18n,
    products,
    moneyFormat,
    hasRanking = false,
    type,
    onloadMore,
    loading,
    hideLoadMore,
    page,
    perPage
  } = props;
  const [openDetail, setOpenDetail] = React.useState(false);
  const [detail, setDetail] = React.useState(undefined);
  const nextPageRef = createRef();

  React.useLayoutEffect(() => {
    if (page > 1) {
      nextPageRef?.current &&
        nextPageRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'start',
          inline: 'nearest'
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  if (loading) {
    return (
      <div className="BC-Component-ProductList">
        {[...Array(10).keys()].map(i => (
          <div className="product" style={{ gap: '10px', alignItems: 'start' }}>
            <div style={{ width: '30%' }}>
              <SkeletonDisplayText size="small" />
            </div>
            <div style={{ width: '70%' }}>
              <SkeletonBodyText />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const handleOpenDetail = product => {
    setDetail(product);
    setOpenDetail(true);
  };

  const renderRankingView = posChange => {
    if (!hasRanking) return null;
    let clsName = '';
    let icon = null;
    if (posChange > 0) {
      clsName = 'product-ranking-up';
      icon = ArrowUpMinor;
    }
    if (posChange < 0) {
      clsName = 'product-ranking-down';
      icon = ArrowDownMinor;
    }
    return (
      <div className={`product-ranking ${clsName}`}>
        {icon && <Icon source={icon} color="subdued" />}
        <p>{!posChange ? '-' : Math.abs(posChange)}</p>
      </div>
    );
  };
  return (
    <div className="BC-Component-ProductList">
      {products && products.length === 0 && (
        <div className="no-product">
          <span>{i18n.translate('AdminHTML.Product.Product.noResult')}</span>
        </div>
      )}
      {type === 'ranking' && (
        <div className="product">
          <div className="product-no">#</div>
          {hasRanking && (
            <div>{i18n.translate('AdminHTML.TestYourSearch.Table.CompareView.rank')}</div>
          )}
          <div className="product-info">
            {i18n.translate('AdminHTML.TestYourSearch.Table.CompareView.product')}
          </div>
        </div>
      )}
      {products &&
        products.map((product, index) => {
          return (
            <div
              key={index}
              ref={index === (page - 1) * perPage ? nextPageRef : undefined}
              className="product"
              onClick={() => handleOpenDetail(product)}>
              <div className="product-no">{index + 1}.</div>
              {renderRankingView(product.posChange)}
              <div className="product-image">
                <img src={(product.images || {})['1'] || noProductImage} alt="product" />
              </div>
              <div className="product-info">
                <span className="product-title">{product.title}</span>
                <ProductPrice product={product} moneyFormat={moneyFormat} />
              </div>
              <div className="hover-view">
                <Icon source={ViewMajor} color="base" />
              </div>
            </div>
          );
        })}
      {!hideLoadMore && (
        <div className="load-more">
          <Link removeUnderline onClick={onloadMore}>
            {i18n.translate('AdminHTML.Product.Product.loadMore')}
          </Link>
        </div>
      )}
      <Modal
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        title={i18n.translate('AdminHTML.Product.Product.detail')}>
        <Modal.Section>
          <ProductDetail product={detail} moneyFormat={moneyFormat} />
        </Modal.Section>
      </Modal>
    </div>
  );
};

export default withI18n()(ProductList);
